import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { createCommercialTerms } from '../redux/actions/commonActions';

export default function CreateCommercialTermsModal({ open, onClose }) {

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      name: '',
      slabs: [
        { minCtc: 0, maxCtc: '', percentage: '' }
      ]
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      slabs: Yup.array()
        .of(
          Yup.object({
            minCtc: Yup.number().min(0).required(),
            maxCtc: Yup.number()
              .nullable()
              .moreThan(
                Yup.ref('minCtc'),
                'Max CTC must be greater than Min CTC'
              ),
            percentage: Yup.number()
              .min(0)
              .max(100)
              .required()
          })
        )
        .min(1, 'At least one slab required')
    }),
    onSubmit: async (values) => {
      let payload = {
        'name': values.name,
        'slabs': values.slabs
      }
      dispatch(createCommercialTerms(payload));
      onClose();
    }
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Commercial Terms</DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          label="Terms Name"
          margin="normal"
          {...formik.getFieldProps('name')}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />

        <Box mt={2}>
          {formik.values.slabs.map((slab, index) => (
            <Stack
              key={index}
              direction="row"
              spacing={1}
              alignItems="center"
              mb={1}
            >
              <TextField
                label="Min CTC"
                type="number"
                size="small"
                value={slab.minCtc}
                onChange={(e) =>
                  formik.setFieldValue(
                    `slabs.${index}.minCtc`,
                    e.target.value
                  )
                }
                error={
                  !!formik.errors.slabs?.[index]?.minCtc
                }
              />

              <TextField
                label="Max CTC"
                type="number"
                size="small"
                value={slab.maxCtc}
                onChange={(e) =>
                  formik.setFieldValue(
                    `slabs.${index}.maxCtc`,
                    e.target.value
                  )
                }
                error={
                  !!formik.errors.slabs?.[index]?.maxCtc
                }
              />

              <TextField
                label="%"
                type="number"
                size="small"
                value={slab.percentage}
                onChange={(e) =>
                  formik.setFieldValue(
                    `slabs.${index}.percentage`,
                    e.target.value
                  )
                }
                error={
                  !!formik.errors.slabs?.[index]?.percentage
                }
              />

              <IconButton
                disabled={formik.values.slabs.length === 1}
                onClick={() => {
                  const slabs = [...formik.values.slabs];
                  slabs.splice(index, 1);
                  formik.setFieldValue('slabs', slabs);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Stack>
          ))}

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() =>
              formik.setFieldValue('slabs', [
                ...formik.values.slabs,
                { minCtc: '', maxCtc: '', percentage: '' }
              ])
            }
          >
            Add Slab
          </Button>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={formik.handleSubmit}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
